import { useEffect } from 'react';
import type { LucideIcon } from 'lucide-react';
import {
  X,
  Pencil,
  Settings2,
  Percent,
  Clock,
  CalendarOff,
  Star,
  Building2,
  CalendarDays,
  Hash,
  Timer,
  Coins,
  CheckCircle2,
  XCircle,
  Info,
} from 'lucide-react';
import type { DailyRateBasis, PayrollPolicy } from '../../../services/httpServices/hrService';

const BASIS_LABEL: Record<DailyRateBasis, string> = {
  CALENDAR_DAYS: 'Calendar days',
  FIXED_DAYS: 'Fixed working days',
  WORKING_DAYS: 'Actual working days',
};

const WEEKDAYS: Record<number, string> = {
  0: 'Sunday',
  1: 'Monday',
  2: 'Tuesday',
  3: 'Wednesday',
  4: 'Thursday',
  5: 'Friday',
  6: 'Saturday',
};

function formatDateTime(value?: string | null) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('en-GB', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function StatTile({
  icon: Icon,
  label,
  value,
  hint,
}: {
  icon: LucideIcon;
  label: string;
  value: React.ReactNode;
  hint?: string;
}) {
  return (
    <div className="rounded-xl border border-border bg-background/60 p-3">
      <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-text-muted">
        <Icon size={12} className="text-primary" />
        {label}
      </div>
      <p className="mt-1.5 text-sm font-semibold text-text break-words">{value}</p>
      {hint && <p className="mt-0.5 text-[11px] text-text-muted">{hint}</p>}
    </div>
  );
}

function DetailRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-3 text-sm">
      <dt className="text-text-muted">{label}</dt>
      <dd className="text-right font-medium text-text">{value}</dd>
    </div>
  );
}

function Section({
  icon: Icon,
  title,
  description,
  children,
}: {
  icon: LucideIcon;
  title: string;
  description?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-2xl border border-border bg-background/40 p-4">
      <header className="mb-3 flex items-start gap-2.5">
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10">
          <Icon size={15} className="text-primary" />
        </span>
        <div className="min-w-0">
          <h3 className="text-sm font-semibold text-text">{title}</h3>
          {description && <p className="text-xs text-text-muted">{description}</p>}
        </div>
      </header>
      {children}
    </section>
  );
}

interface PayrollPolicyDetailsModalProps {
  policy: PayrollPolicy | null;
  onClose: () => void;
  onEdit?: (policy: PayrollPolicy) => void;
}

/**
 * Read-only, modern summary of a payroll policy. Opened from the policy name
 * in the list so the full configuration can be reviewed without entering the
 * edit workflow.
 */
export function PayrollPolicyDetailsModal({ policy, onClose, onEdit }: PayrollPolicyDetailsModalProps) {
  // Close on Escape for a polished, keyboard-friendly experience. Hook runs
  // unconditionally (before the early return) to respect the rules of hooks.
  useEffect(() => {
    if (!policy) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [policy, onClose]);

  if (!policy) return null;

  const scope = policy.department?.name || (policy.isDefault ? 'All departments' : 'Global default');
  const offDays = Array.isArray(policy.weeklyOffDays) ? policy.weeklyOffDays : [];
  const hasOffDays = offDays.length > 0;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-label={`${policy.name} details`}
      onClick={onClose}
    >
      <div
        className="flex max-h-[92vh] w-full max-w-4xl flex-col overflow-hidden rounded-2xl border border-border bg-surface shadow-2xl"
        onClick={(event) => event.stopPropagation()}
      >
        {/* Header */}
        <div className="border-b border-border bg-gradient-to-br from-primary/10 via-primary/[0.03] to-transparent p-5">
          <div className="flex items-start justify-between gap-3">
            <div className="flex min-w-0 items-start gap-3">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10 ring-1 ring-primary/20">
                <Settings2 size={20} className="text-primary" />
              </span>
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <h2 className="truncate text-xl font-bold text-text">{policy.name}</h2>
                  {policy.isDefault && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-medium text-primary">
                      <Star size={10} />
                      Default
                    </span>
                  )}
                  <span
                    className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${
                      policy.isActive ? 'bg-success/10 text-success' : 'bg-danger/10 text-danger'
                    }`}
                  >
                    {policy.isActive ? <CheckCircle2 size={11} /> : <XCircle size={11} />}
                    {policy.isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <p className="mt-1 flex items-center gap-1.5 text-xs text-text-muted">
                  <Building2 size={12} />
                  {scope}
                  <span className="text-text-muted/60">·</span>
                  Updated {formatDateTime(policy.updatedAt || policy.createdAt)}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="shrink-0 rounded-lg p-1.5 text-text-muted transition-colors hover:bg-background hover:text-text"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 space-y-5 overflow-y-auto p-5">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <StatTile
              icon={Percent}
              label="Daily rate"
              value={BASIS_LABEL[policy.dailyRateBasis] ?? policy.dailyRateBasis}
              hint={
                policy.dailyRateBasis === 'FIXED_DAYS'
                  ? `${Number(policy.fixedWorkingDays ?? 0)} fixed days`
                  : undefined
              }
            />
            <StatTile
              icon={Timer}
              label="Standard hours"
              value={`${Number(policy.standardHoursPerDay ?? 0)} h / day`}
              hint="Hourly rate divisor"
            />
            <StatTile
              icon={Clock}
              label="Overtime"
              value={`${Number(policy.overtimeMultiplier ?? 0)}× normal`}
              hint={`${Number(policy.holidayOvertimeMultiplier ?? 0)}× on holidays`}
            />
            <StatTile
              icon={CalendarOff}
              label="Weekly off"
              value={hasOffDays ? `${offDays.length} day${offDays.length === 1 ? '' : 's'}` : 'None'}
              hint="Fallback only"
            />
          </div>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <Section icon={Percent} title="Pay rates" description="How the daily and hourly rate is derived.">
              <dl className="space-y-2">
                <DetailRow label="Basis" value={BASIS_LABEL[policy.dailyRateBasis] ?? policy.dailyRateBasis} />
                <DetailRow label="Fixed working days" value={Number(policy.fixedWorkingDays ?? 0)} />
                <DetailRow label="Standard hours / day" value={`${Number(policy.standardHoursPerDay ?? 0)} h`} />
                <DetailRow
                  label="Prorate allowances"
                  value={policy.prorateAllowances ? 'Yes, by payable days' : 'No, paid in full'}
                />
              </dl>
            </Section>

            <Section icon={Coins} title="Deductions & leave" description="Loss-of-pay rules and protected net pay.">
              <dl className="space-y-2">
                <DetailRow
                  label="Absence multiplier"
                  value={`${Number(policy.absentDeductionMultiplier ?? 0)}× per day`}
                />
                <DetailRow label="Late penalty" value={`${Number(policy.latePenaltyDays ?? 0)} day(s)`} />
                <DetailRow
                  label="Unapproved absence"
                  value={policy.deductUnapprovedAbsence !== false ? 'Deducted' : 'Not deducted'}
                />
                <DetailRow
                  label="Minimum net pay"
                  value={
                    Number(policy.minimumNetPay ?? 0) > 0
                      ? `৳${Number(policy.minimumNetPay).toLocaleString()}`
                      : 'No floor'
                  }
                />
              </dl>
            </Section>
          </div>

          <Section icon={Clock} title="Overtime" description="Multipliers, caps and rounding applied to overtime pay.">
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
              <StatTile
                icon={Clock}
                label="Normal day"
                value={`${Number(policy.overtimeMultiplier ?? 0)}×`}
              />
              <StatTile
                icon={Clock}
                label="Holiday"
                value={`${Number(policy.holidayOvertimeMultiplier ?? 0)}×`}
              />
              <StatTile
                icon={Timer}
                label="Max / period"
                value={
                  Number(policy.maxOvertimeHours ?? 0) > 0 ? `${Number(policy.maxOvertimeHours)} h` : 'Unlimited'
                }
              />
              <StatTile
                icon={Timer}
                label="Max / day"
                value={
                  Number(policy.maxOvertimeHoursPerDay ?? 0) > 0
                    ? `${Number(policy.maxOvertimeHoursPerDay)} h`
                    : 'Unlimited'
                }
              />
              <StatTile
                icon={Timer}
                label="Max / week"
                value={
                  Number(policy.maxOvertimeHoursPerWeek ?? 0) > 0
                    ? `${Number(policy.maxOvertimeHoursPerWeek)} h`
                    : 'Unlimited'
                }
              />
              <StatTile
                icon={Clock}
                label="Grace"
                value={`${Number(policy.overtimeGraceMinutes ?? 0)} min`}
                hint="Shorter OT ignored"
              />
              <StatTile
                icon={Clock}
                label="Rounding"
                value={
                  Number(policy.overtimeRoundingMinutes ?? 0) > 0
                    ? `${Number(policy.overtimeRoundingMinutes)} min`
                    : 'None'
                }
                hint="Rounded down"
              />
              <StatTile
                icon={Clock}
                label="Minimum payable"
                value={`${Number(policy.minimumOvertimeMinutes ?? 0)} min`}
              />
            </div>
          </Section>

          <Section
            icon={CalendarOff}
            title="Default weekly off days"
            description="Fallback days used when an employee has no off days on their profile."
          >
            {hasOffDays ? (
              <div className="flex flex-wrap gap-2">
                {offDays.map((day) => (
                  <span
                    key={day}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-surface px-2.5 py-1 text-xs font-medium text-text"
                  >
                    <CalendarDays size={12} className="text-primary" />
                    {WEEKDAYS[day] ?? `Day ${day}`}
                  </span>
                ))}
              </div>
            ) : (
              <p className="text-sm text-text-muted">No fallback weekly off days selected.</p>
            )}
          </Section>

          <div className="flex flex-wrap items-center gap-x-5 gap-y-2 rounded-xl border border-border bg-background/40 px-4 py-3 text-xs text-text-muted">
            <span className="inline-flex items-center gap-1.5">
              <Hash size={12} />
              ID: <span className="font-mono text-text">{policy.id}</span>
            </span>
            <span className="inline-flex items-center gap-1.5">
              <CalendarDays size={12} />
              Created {formatDateTime(policy.createdAt)}
            </span>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 border-t border-border p-4">
          <span className="hidden items-center gap-1.5 text-xs text-text-muted sm:flex">
            <Info size={13} />
            Changes apply to future payroll runs
          </span>
          <div className="ml-auto flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-border px-4 py-2 text-sm font-medium text-text-muted transition-colors hover:bg-background hover:text-text"
            >
              Close
            </button>
            {onEdit && (
              <button
                type="button"
                onClick={() => onEdit(policy)}
                className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-dark"
              >
                <Pencil size={15} />
                Edit policy
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
